<template>
  <div class="container-fluid py-4 dashboard-boutique">

    <!-- ================= HEADER ================= -->
    <div class="header-boutique d-flex justify-content-between align-items-center flex-wrap mb-4">

      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-speedometer2 fs-2"></i>

        <div>
          <h2 class="fw-bold mb-0">Dashboard Boutique</h2>

          <small>
            Suivi global des ventes, stock et performance
            <span class="badge bg-light text-dark ms-2">
              {{ libellePeriode }}
            </span>
            <span class="badge bg-light text-dark ms-1" v-if="dashboard.dateDebut">
              {{ formatDate(dashboard.dateDebut) }} - {{ formatDate(dashboard.dateFin) }}
            </span>
          </small>
        </div>
      </div>

      <!-- ================= FILTRES ================= -->
      <div class="d-flex align-items-center gap-2 flex-wrap">

        <!-- principaux -->
        <button
          v-for="periode in periodesPrincipales"
          :key="periode.value"
          class="btn"
          :class="filtre === periode.value ? 'btn-active' : 'btn-outline-light'"
          @click="changerFiltre(periode.value)"
        >
          {{ periode.label }}
        </button>

        <!-- secondaires -->
        <div class="dropdown">
          <button
            class="btn btn-outline-light dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
          >
            Plus
          </button>

          <ul class="dropdown-menu dropdown-menu-end shadow">
            <li v-for="periode in periodesSecondaires" :key="periode.value">
              <a
                class="dropdown-item"
                href="#"
                :class="{ 'active': filtre === periode.value }"
                @click.prevent="changerFiltre(periode.value)"
              >
                {{ periode.label }}
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- ================= LOADER ================= -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-success"></div>
      <p>Chargement...</p>
    </div>

    <!-- ================= CONTENT ================= -->
    <div v-else>

      <!-- ================= STOCK ================= -->
      <h5 class="section-title">
        <i class="bi bi-boxes me-2"></i>Stock
      </h5>

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="card in stockKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>
              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= DETAIL RUPTURES / STOCK FAIBLE ================= -->
      <div class="row g-3 mb-4">

        <div class="col-lg-6">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-3 text-danger">
                <i class="bi bi-x-octagon me-1"></i> Produits en rupture
              </h6>

              <div v-if="produitsRupture.length === 0" class="text-muted small py-3 text-center">
                Aucun produit en rupture actuellement.
              </div>

              <div v-else class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th class="text-end">Stock</th>
                    
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in produitsRupture" :key="p.id">
                      <td>{{ p.nom }}</td>
                      <td class="text-end text-danger fw-bold">
                        {{ quantite(p.stock) }} {{ p.uniteBase }}
                      </td>
                     
                      
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-3 text-warning">
                <i class="bi bi-exclamation-triangle me-1"></i> Stock faible
              </h6>

              <div v-if="produitsStockFaible.length === 0" class="text-muted small py-3 text-center">
                Aucune alerte de stock faible actuellement.
              </div>

              <div v-else class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th class="text-end">Stock</th>
                      <th class="text-end">Seuil</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in produitsStockFaible" :key="p.id">
                      <td>{{ p.nom }}</td>
                      <td class="text-end text-warning fw-bold">
                        {{ quantite(p.stock) }} {{ p.uniteBase }}
                      </td>
                      <td class="text-end text-muted">
                        {{ quantite(p.seuil) }}
                      </td>
                      <td class="text-end">
                        <router-link
                          :to="{ name: 'edit_produit', params: { id: p.id } }"
                          class="btn btn-sm btn-outline-warning"
                        >
                          Voir
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- ================= VENTES ================= -->
      <h5 class="section-title">
        <i class="bi bi-cart-check me-2"></i>Ventes
      </h5>

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="card in ventesKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>
              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= GRAPHIQUES VENTES ================= -->
      <div class="row g-3 mb-4">

        <div class="col-lg-8">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Évolution des ventes</h6>
              <div v-if="!hasVentesParJour" class="text-muted small py-4 text-center">
                Aucune vente sur cette période.
              </div>
              <canvas v-show="hasVentesParJour" ref="ventesChart" height="90"></canvas>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Ventes par catégorie</h6>
              <div v-if="!hasParCategorie" class="text-muted small py-4 text-center">
                Aucune donnée sur cette période.
              </div>
              <canvas v-show="hasParCategorie" ref="categorieChart" height="200"></canvas>
            </div>
          </div>
        </div>

      </div>

      <!-- ================= TOP PRODUITS ================= -->
      <div class="row g-3 mb-4">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Top 5 produits vendus</h6>
              <div v-if="!hasTopProduits" class="text-muted small py-4 text-center">
                Aucune vente sur cette période.
              </div>
              <canvas v-show="hasTopProduits" ref="topProduitsChart" height="80"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= APPRO ================= -->
      <h5 class="section-title">
        <i class="bi bi-truck me-2"></i>Approvisionnement
      </h5>

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="card in approKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>
              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= RENTABILITE ================= -->
      <h5 class="section-title" v-if="$store.getters.hasPermission('DASH_VIEW_RENTABILITE')">
        <i class="bi bi-graph-up-arrow me-2"></i>Rentabilité
      </h5>

      <div class="row g-3 mb-4" v-if="$store.getters.hasPermission('DASH_VIEW_RENTABILITE')">
        <div
          class="col-md-3"
          v-for="card in rentabiliteKpi"
          :key="card.title"
        >
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>

              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= VENTES VS ACHATS ================= -->
      <div class="row g-3 mb-4" v-if="$store.getters.hasPermission('DASH_VIEW_RENTABILITE')">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Ventes vs Achats</h6>
              <canvas ref="ventesAchatsChart" height="70"></canvas>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/api";
import dayjs from "dayjs";
import Chart from "chart.js/auto";

export default {
  data() {
    return {
      isLoading: false,
      filtre: "jour",

      dashboard: {
        stock: {
          produitsRupture: [],
          produitsStockFaible: []
        },
        ventes: {},
        approvisionnement: {},
        rentabilite: {},
        dateDebut: null,
        dateFin: null,
        charts: {
          ventesParJour: [],
          parCategorie: [],
          topProduits: [],
          ventesVsAchats: { ventes: 0, achats: 0 }
        }
      },

      chartInstances: {},

      periodesPrincipales: [
        { value: 'jour', label: "Aujourd'hui" },
        { value: 'semaine', label: 'Cette semaine' },
        { value: 'mois', label: 'Ce mois-ci' },
        { value: 'annee', label: 'Cette année' }
      ],

      periodesSecondaires: [
        { value: 'hier', label: 'Hier' },
        { value: 'semaine_derniere', label: 'Semaine dernière' },
        { value: 'mois_dernier', label: 'Mois dernier' },
        { value: 'annee_derniere', label: 'Année dernière' }
      ]
    };
  },

  computed: {

    libellePeriode() {
      const all = [
        ...this.periodesPrincipales,
        ...this.periodesSecondaires
      ];
      return all.find(p => p.value === this.filtre)?.label || this.filtre;
    },

    stockKpi() {
      const s = this.dashboard.stock || {};
      return [
        { title: "Articles", value: s.articles ?? 0, icon: "bi bi-box bg-primary" },
        { title: "Lots", value: s.lots ?? 0, icon: "bi bi-box bg-primary" },
        { title: "Ruptures", value: s.ruptures ?? 0, icon: "bi bi-exclamation-triangle bg-danger" },
        { title: "Stock critique", value: s.stock_critique ?? 0, icon: "bi bi-exclamation-circle bg-warning" }
      ];
    },

    ventesKpi() {
      const v = this.dashboard.ventes || {};
      return [
        { title: "Total ventes", value: v.total ?? 0, icon: "bi bi-cart bg-primary" },
        { title: "CA", value: this.money(v.chiffre_affaires), icon: "bi bi-currency-exchange bg-success" },
        { title: "Annulées", value: v.annulees ?? 0, icon: "bi bi-x-circle bg-danger" },
        { title: "Quantité", value: this.quantite(v.quantite_vendue), icon: "bi bi-boxes bg-info" }
      ];
    },

    approKpi() {
      const a = this.dashboard.approvisionnement || {};
      return [
        { title: "Entrées", value: a.total ?? 0, icon: "bi bi-arrow-down bg-primary" },
        { title: "Quantité", value: this.quantite(a.quantite_entree), icon: "bi bi-stack bg-success" },
        { title: "Valeur", value: this.money(a.valeur), icon: "bi bi-wallet bg-warning" }
      ];
    },

    rentabiliteKpi() {
      const r = this.dashboard.rentabilite || {}
      const chiffreAffaires = Number(r.chiffre_affaires || 0)
      const coutAchat = Number(r.cout_achat || 0)
      const benefice = chiffreAffaires - coutAchat

      const marge = chiffreAffaires > 0
        ? ((benefice / chiffreAffaires) * 100).toFixed(2)
        : 0

      return [
        { title: "CA", value: this.money(r.chiffre_affaires), icon: "bi bi-currency-exchange bg-primary" },
        { title: "Coût Achat", value: this.money(r.cout_achat), icon: "bi bi-cart-dash bg-warning" },
        { title: "Bénéfice", value: this.money(benefice), icon: "bi bi-graph-up-arrow bg-success" },
        { title: "Marge", value: `${marge}%`, icon: "bi bi-percent bg-info" },
        { title: "Valeur stock", value: this.money(r.valeur_stock), icon: "bi bi-cash bg-success" },
      ]
    },

    produitsRupture() {
      return this.dashboard.stock?.produitsRupture || []
    },

    produitsStockFaible() {
      return this.dashboard.stock?.produitsStockFaible || []
    },

    hasVentesParJour() {
      return (this.dashboard.charts?.ventesParJour?.length || 0) > 0
    },

    hasParCategorie() {
      return (this.dashboard.charts?.parCategorie?.length || 0) > 0
    },

    hasTopProduits() {
      return (this.dashboard.charts?.topProduits?.length || 0) > 0
    }
  },

  mounted() {
    this.load();
  },

  beforeUnmount() {
    this.destroyCharts();
  },

  methods: {

    async load() {
      this.isLoading = true;

      try {
        const res = await api.getItems(
          `dashboard_data?periode=${this.filtre}`
        );

        this.dashboard = res?.data || res || {
          stock: { produitsRupture: [], produitsStockFaible: [] },
          ventes: {},
          approvisionnement: {},
          rentabilite: {},
          dateDebut: null,
          dateFin: null,
          charts: { ventesParJour: [], parCategorie: [], topProduits: [], ventesVsAchats: { ventes: 0, achats: 0 } }
        };

      } catch (e) {
        console.error(e);
        alert("Erreur lors du chargement du tableau de bord");
      } finally {
        this.isLoading = false;
        this.$nextTick(() => this.renderCharts());
      }
    },

    changerFiltre(value) {
      this.filtre = value;
      this.load();
    },

    money(v) {
      return Number(v || 0).toLocaleString("fr-FR") + " FCFA";
    },

    quantite(v) {
      const n = Number(v || 0)
      if (Number.isInteger(n)) return n.toString()
      return n.toFixed(3).replace(/\.?0+$/, '')
    },

    formatDate(d) {
      return d ? dayjs(d).format('DD/MM/YYYY') : ''
    },

    destroyCharts() {
      Object.values(this.chartInstances).forEach(c => c?.destroy())
      this.chartInstances = {}
    },

    renderCharts() {
      this.destroyCharts()

      const charts = this.dashboard.charts || {}

      if (this.hasVentesParJour && this.$refs.ventesChart) {
        this.chartInstances.ventes = new Chart(this.$refs.ventesChart, {
          type: 'line',
          data: {
            labels: charts.ventesParJour.map(v => dayjs(v.jour).format('DD/MM')),
            datasets: [{
              label: 'Ventes (FCFA)',
              data: charts.ventesParJour.map(v => Number(v.total)),
              borderColor: '#198754',
              backgroundColor: 'rgba(25,135,84,0.1)',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true } }
          }
        })
      }

      if (this.hasParCategorie && this.$refs.categorieChart) {
        this.chartInstances.categorie = new Chart(this.$refs.categorieChart, {
          type: 'doughnut',
          data: {
            labels: charts.parCategorie.map(c => c.nom),
            datasets: [{
              data: charts.parCategorie.map(c => Number(c.total)),
              backgroundColor: ['#198754','#3b82f6','#f59e0b','#ef4444','#8b5cf6','#14b8a6']
            }]
          },
          options: { responsive: true, plugins: { legend: { position: 'bottom' } } }
        })
      }

      if (this.hasTopProduits && this.$refs.topProduitsChart) {
        this.chartInstances.topProduits = new Chart(this.$refs.topProduitsChart, {
          type: 'bar',
          data: {
            labels: charts.topProduits.map(p => p.nom),
            datasets: [{
              label: 'Chiffre d\'affaires (FCFA)',
              data: charts.topProduits.map(p => Number(p.total)),
              backgroundColor: '#3b82f6'
            }]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            plugins: { legend: { display: false } },
            scales: { x: { beginAtZero: true } }
          }
        })
      }

      if (this.$refs.ventesAchatsChart) {
        const va = charts.ventesVsAchats || { ventes: 0, achats: 0 }
        this.chartInstances.ventesAchats = new Chart(this.$refs.ventesAchatsChart, {
          type: 'bar',
          data: {
            labels: [this.libellePeriode],
            datasets: [
              { label: 'Ventes', data: [Number(va.ventes)], backgroundColor: '#198754' },
              { label: 'Achats', data: [Number(va.achats)], backgroundColor: '#dc3545' }
            ]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            plugins: { legend: { position: 'top' } },
            scales: { x: { beginAtZero: true } }
          }
        })
      }
    }
  }
};
</script>

<style scoped>
.dashboard-boutique {
  background: #f5f7fb;
  min-height: 100vh;
}

.header-boutique {
  background: linear-gradient(135deg, #0f5132, #198754);
  color: white;
  padding: 20px;
  border-radius: 18px;
}

.section-title {
  margin: 20px 0 10px;
  font-weight: bold;
  color: #333;
}

.card-kpi {
  border: none;
  border-radius: 14px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: 0.2s;
}

.card-kpi:hover {
  transform: translateY(-4px);
}

.card-kpi i {
  font-size: 28px;
  padding: 10px;
  border-radius: 12px;
  color: white;
}

.btn-active {
  background: white;
  color: #198754;
  font-weight: bold;
}
</style>